import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { referralPartnerApi } from '@/api/referralPartner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2 } from 'lucide-react';

export type PartnerHospitalLink = {
  id: string;
  name: string;
  address?: string;
};

type Props = {
  value: string;
  onChange: (hospitalId: string) => void;
  /** Dark portal styling */
  variant?: 'portal' | 'default';
  disabled?: boolean;
};

export default function PartnerHospitalSelect({
  value,
  onChange,
  variant = 'portal',
  disabled,
}: Props) {
  const { data: hospitals, isLoading } = useQuery({
    queryKey: ['partner-hospitals'],
    queryFn: async () => {
      const res = await referralPartnerApi.availableHospitals();
      return (res.data?.data ?? []) as PartnerHospitalLink[];
    },
  });

  useEffect(() => {
    if (!value && hospitals?.length === 1) {
      onChange(hospitals[0].id);
    }
  }, [hospitals, value, onChange]);

  const isPortal = variant === 'portal';
  const triggerClass = isPortal
    ? 'border-white/10 bg-slate-900 text-white h-11'
    : '';

  if (isLoading) {
    return (
      <p className={isPortal ? 'text-sm text-slate-500' : 'text-sm text-gray-500'}>
        Loading hospitals…
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <label
        className={`flex items-center gap-1.5 text-sm font-medium ${
          isPortal ? 'text-slate-300' : 'text-gray-700'
        }`}
      >
        <Building2 className="h-4 w-4 text-cyan-400" />
        Refer to hospital <span className="text-red-400">*</span>
      </label>
      <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className={triggerClass}>
          <SelectValue placeholder="Choose hospital for this referral" />
        </SelectTrigger>
        <SelectContent className={isPortal ? 'bg-slate-900 text-white border-white/10' : ''}>
          {hospitals?.map((row) => (
            <SelectItem key={row.id} value={row.id}>
              {row.address ? `${row.name} — ${row.address}` : row.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className={`text-xs ${isPortal ? 'text-slate-500' : 'text-gray-500'}`}>
        Select which hospital should receive this patient referral.
      </p>
    </div>
  );
}
